import { useState, useMemo, useContext } from 'react';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { Typography } from '../../../../../Typography';
import { Box } from '../../../../../Box';
import { InputTooltip } from '../../../../../InputTooltip';
import { List, ListItem } from '../../../../../List';
import { TextInput } from '../../../../../input';
import { useForm } from '../../hooks';
import { WysiwygContext } from '../../../context';

interface AddVideoDialogProps {
  open: boolean;
  onClose: () => void;
  addVideo: (src: string, description: string) => void;
}

interface AddVideoDialogForm {
  src: string;
}

export const AddVideoDialog = ({
  open,
  onClose,
  addVideo,
}: AddVideoDialogProps) => {
  const [src, setSrc] = useState('');
  const [description, setDescription] = useState('');
  const validator = useValidationService();
  const { envVars } = useContext(WysiwygContext);

  const allValues = useMemo(() => {
    return {
      src,
    };
  }, [src]);

  const fieldValidators = useMemo(() => {
    return {
      src: [
        validator.required<AddVideoDialogForm>(),
        validator.validUrl(),
        (value?: string) => {
          if (!value) return undefined;

          try {
            const { hostname } = new URL(value);
            if (
              hostname === 'rutube.sport' ||
              hostname.endsWith('.rutube.sport')
            ) {
              return 'Добавление видео с rutube.sport не поддерживается';
            }
          } catch {
            return undefined;
          }

          return undefined;
        },
      ],
    };
  }, [validator]);

  const {
    errors,
    touched,
    validateField,
    hasValidationErrors,
    showError,
    handleBlur,
  } = useForm<AddVideoDialogForm>(allValues, fieldValidators);

  const handleChangeSrcInput = async (value?: string) => {
    setSrc(() => value || '');
    await validateField('src', value);
    showError('src');
  };

  const handleChangeDescriptionInput = (value?: string) => {
    setDescription(() => value || '');
  };

  const handleSubmit = () => {
    onClose();
    addVideo(src, description);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogContent
          title="Добавление видео по ссылке"
          onClose={onClose}
          actions={
            <>
              <Button variant="tertiaryLight" size="large" onClick={onClose}>
                Отменить
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={hasValidationErrors}
              >
                Добавить
              </Button>
            </>
          }
        >
          <Box display="flex" flexDirection="column" gap="24">
            <Typography variant="body1" color="text.light.secondary">
              После добавления ссылки видео будет отображаться и
              воспроизводиться в полном размере
            </Typography>

            <Box display="flex" flexDirection="column" gap="8">
              <TextInput
                required
                name="src"
                label="Ссылка на видео"
                placeholder="Адрес ссылки"
                onChange={handleChangeSrcInput}
                error={touched.src && !!errors.src}
                helperText={touched.src ? errors.src : undefined}
                onBlur={handleBlur('src')}
                tooltip={
                  <InputTooltip description="Доступные платформы для добавления ссылки на видео:">
                    <Box display="flex" flexDirection="column" gap="12">
                      <List pl={2} listStyleType="disc">
                        <Box display="flex" flexDirection="column" gap="8">
                          <ListItem>VK Видео</ListItem>
                          <ListItem>
                            {'Rutube (кроме домена rutube.sport)'}
                          </ListItem>
                        </Box>
                      </List>
                      {envVars.allowDirectVideoLinks && (
                        <Typography variant="caption" color="bg.light.primary">
                          Доступно добавление прямой ссылки на видео
                        </Typography>
                      )}
                    </Box>
                  </InputTooltip>
                }
              />
              <TextInput
                name="description"
                label="Подпись"
                placeholder="Подпись к видео"
                onChange={handleChangeDescriptionInput}
              />
            </Box>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};
